import type { ReservationStatus } from './reservation-status'
import { atBusinessTime, businessDateOf, parseLocalDate } from './time'
import type { DeliveryLeg } from './vocabulary'

/**
 * S9 (LOC-5): whether a booking's delivery or collection is still to be
 * driven: the delivery until the car is handed over, the collection until it
 * comes back. Nothing is written on a task once its leg is done but the
 * removal of its text, and a person still holding a pending one cannot be
 * deactivated. Migration 0049's `delivery_leg_pending` says the same, and a
 * test keeps the two equal.
 */
export function deliveryLegPending(leg: DeliveryLeg, status: ReservationStatus): boolean {
  return status === 'confirmed' || (leg === 'collection' && status === 'checked_out')
}

/**
 * Where a trip stands on Today and the booking page: nobody on it yet,
 * someone on it, or driven. Whether it is late is a separate fact: a trip
 * nobody has taken can be late too, and the screen says both.
 */
export const DELIVERY_STATES = ['to_assign', 'assigned', 'done'] as const
export type DeliveryState = (typeof DELIVERY_STATES)[number]

export interface DeliveryFacts {
  leg: DeliveryLeg
  /** The booking's status. */
  status: ReservationStatus
  /** Someone is on the trip. */
  assigned: boolean
  /** When the trip is due: the booking's start for a delivery, its end for a collection. */
  dueAt: Date
}

/** Where a booking stands once its car went out: a trip no longer pending was driven. */
const DRIVEN: readonly ReservationStatus[] = ['checked_out', 'returned', 'closed']

/**
 * A trip still to be driven is to assign or assigned, and late once its time
 * has passed; a trip driven is done. Null for one that never happens: the
 * booking was cancelled, its customer never came, or it is still a draft.
 */
export function deliveryState(
  facts: DeliveryFacts,
  now: Date,
): { state: DeliveryState; late: boolean } | null {
  if (deliveryLegPending(facts.leg, facts.status)) {
    return {
      state: facts.assigned ? 'assigned' : 'to_assign',
      late: now.getTime() > facts.dueAt.getTime(),
    }
  }
  // Past its pending window with the car gone out: driven. A collection is
  // still pending while the car is out, so it is done from the return on.
  return DRIVEN.includes(facts.status) ? { state: 'done', late: false } : null
}

/**
 * S9 (Q-62): how long a trip given to someone lets them act on its booking:
 * until the end of the day after it is due (Europe/Rome), so what was done
 * at the quay without signal (cash taken, a document photographed) can still
 * be recorded once there is some. A vehicle photo keeps its own rule: it
 * counts only until just after its stage closes (LATE_PHOTO_GRACE_MS). Then
 * the office takes over.
 */
export function deliveryScopeEnd(dueAt: Date): Date {
  return atBusinessTime(parseLocalDate(businessDateOf(dueAt)).add({ days: 2 }), '00:00')
}

export interface ScopeTrip {
  leg: DeliveryLeg
  status: ReservationStatus
  startsAt: Date
  endsAt: Date
  /** That end's delivery zone; null where it is a pick-up point. */
  zoneId: string | null
}

/**
 * Whether a trip given to the person lets them act on its booking now: its
 * end is a delivery zone, the trip happens (the booking was not cancelled,
 * missed or left a draft), and its time is not long past.
 */
export function tripGivesScope(trip: ScopeTrip, now: Date): boolean {
  if (trip.zoneId === null) return false
  const dueAt = trip.leg === 'delivery' ? trip.startsAt : trip.endsAt
  if (deliveryState({ leg: trip.leg, status: trip.status, assigned: true, dueAt }, now) === null) {
    return false
  }
  return now.getTime() < deliveryScopeEnd(dueAt).getTime()
}

/** The leg a booking's money is taken on now: the collection once the car is out, the delivery before. */
export function legOfTheMoment(status: ReservationStatus): DeliveryLeg {
  return DRIVEN.includes(status) ? 'collection' : 'delivery'
}

/**
 * The trip money taken now belongs to, of those a person holds on one
 * booking: the one of the leg of the moment, else the other.
 */
export function tripOfTheMoment<T extends { leg: DeliveryLeg }>(
  trips: readonly T[],
  status: ReservationStatus,
): T | undefined {
  const leg = legOfTheMoment(status)
  return trips.find((trip) => trip.leg === leg) ?? trips.find((trip) => trip.leg !== leg)
}
